import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    this._latestRequestId = 0;

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * Always fetches the real cart total from Shopify.
   *
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const requestId = ++this._latestRequestId;

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Cart request failed: ${response.status}`);
      }

      const cart = await response.json();

      // Ignore stale responses so an older, slower fetch can't overwrite
      // a more recent update.
      if (requestId !== this._latestRequestId) return;

      this.renderCartBubble(cart.item_count);
    } catch (error) {
      console.error('Failed to update cart bubble:', error);

      // Retry once to get the real count instead of guessing with math
      // that can double-count (currentCartCount already reflects the
      // post-add total when the update comes from product-form-component).
      try {
        const retryResponse = await fetch(`${Theme.routes.cart_url}.js`, {
          headers: { Accept: 'application/json' },
        });

        if (retryResponse.ok && requestId === this._latestRequestId) {
          const cart = await retryResponse.json();
          this.renderCartBubble(cart.item_count);
          return;
        }
      } catch (retryError) {
        console.error('Retry to update cart bubble also failed:', retryError);
      }

      // Leave the bubble as-is if we still can't verify the real count.
    }
  };

  /**
   * Renders the cart bubble.
   *
   * @param {number} itemCount - Total number of items in the cart.
   * @param {boolean} animate - Whether to animate the cart bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle(
      'cart-bubble--animating',
      itemCount > 0 && animate
    );

    // Always use the actual total cart count
    this.currentCartCount = itemCount;

    this.classList.toggle(
      'header-actions__cart-icon--has-cart',
      itemCount > 0
    );

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(itemCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;

    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');

    if (sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
